import client from './client'
import type { ApiResponse } from '@/types'
import type { UserRole } from '@/types'

export type PermissionProduct = 'MA3HUB' | 'VERIPAY' | 'BOTH' | 'ADMIN'

export interface PermissionMeta {
  name: string
  label: string
  description: string
  product: PermissionProduct
  dangerous: boolean
  implies: string[]
  request_type: string | null
}

export interface CatalogDomain {
  name: string
  permissions: PermissionMeta[]
}

export interface CatalogResponse {
  domains: CatalogDomain[]
}

export interface CeilingResponse {
  group: UserRole
  floor: string[]
  domains: CatalogDomain[]
}

export type GroupArchetype = 'PLATFORM' | 'BACK_OFFICE' | 'OPERATOR' | 'OWNER'
export type GroupBadgeColor = 'default' | 'success' | 'warning' | 'error' | 'info'

export interface GroupSpec {
  key: UserRole
  label: string
  archetype: GroupArchetype
  level: number
  mobile_route: string | null
  badge_color: GroupBadgeColor
  description: string
}

export interface GroupsResponse {
  groups: GroupSpec[]
}

export interface UserGrants {
  user_id: string
  group: UserRole
  granted: string[]
  effective: string[]
  ceiling: string[]
  floor: string[]
}

export interface PermissionTemplate {
  id: string
  scope: 'PLATFORM' | 'SACCO'
  sacco_id: string | null
  group: UserRole
  permissions: string[]
  updated_by: string | null
  updated_at: string | null
}

export interface EffectiveTemplate {
  group: UserRole
  sacco_id: string | null
  source: 'PLATFORM' | 'SACCO' | 'FALLBACK'
  permissions: string[]
  ceiling: string[]
}

export const permissionsApi = {
  getGroups: async (): Promise<GroupsResponse> => {
    const response = await client.get<ApiResponse<GroupsResponse>>('/permissions/groups')
    return response.data.data!
  },

  getCatalog: async (): Promise<CatalogResponse> => {
    const response = await client.get<ApiResponse<CatalogResponse>>('/permissions/catalog')
    return response.data.data!
  },

  getCeiling: async (group?: UserRole): Promise<CeilingResponse> => {
    const params = group ? { group } : undefined
    const response = await client.get<ApiResponse<CeilingResponse>>('/permissions/ceiling', { params })
    return response.data.data!
  },

  getUserGrants: async (userId: string): Promise<UserGrants> => {
    const response = await client.get<ApiResponse<UserGrants>>(`/users/${userId}/permissions`)
    return response.data.data!
  },

  replaceUserGrants: async (userId: string, permissions: string[]): Promise<UserGrants> => {
    const response = await client.put<ApiResponse<UserGrants>>(
      `/users/${userId}/permissions`,
      { permissions },
    )
    return response.data.data!
  },

  // --- Templates ---

  listTemplates: async (params?: { scope?: 'PLATFORM' | 'SACCO'; sacco_id?: string }): Promise<{ templates: PermissionTemplate[] }> => {
    const response = await client.get<ApiResponse<{ templates: PermissionTemplate[] }>>(
      '/permissions/templates',
      { params },
    )
    return response.data.data!
  },

  getEffectiveTemplate: async (group: UserRole, saccoId?: string): Promise<EffectiveTemplate> => {
    const params: Record<string, string> = { group }
    if (saccoId) params.sacco_id = saccoId
    const response = await client.get<ApiResponse<EffectiveTemplate>>(
      '/permissions/templates/effective',
      { params },
    )
    return response.data.data!
  },

  upsertTemplate: async (payload: {
    scope: 'PLATFORM' | 'SACCO'
    sacco_id?: string | null
    group: UserRole
    permissions: string[]
  }): Promise<PermissionTemplate> => {
    const response = await client.put<ApiResponse<PermissionTemplate>>(
      '/permissions/templates',
      payload,
    )
    return response.data.data!
  },

  deleteSaccoOverride: async (templateId: string): Promise<void> => {
    await client.delete(`/permissions/templates/${templateId}`)
  },
}
